import produce from "immer";
import { find } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Role } from "../../utils/constants";
import { Member } from "../../utils/types";
import { useAuthentication } from "../authentication";
import { useWorkspaces } from "../workspaces";

interface Props {}

export type ContextValue = {
  members: Member[];
  fetchMembers: () => Promise<Member[]>;
  inviteMember: (emails: string[]) => Promise<
    | ApiResponse<Member[]>
    | {
        status: boolean;
        message: string;
      }
    | undefined
  >;
  updateMember: (member: Member, role: string) => Promise<ApiResponse<Member>>;
  removeMember: (member: Member) => Promise<ApiResponse<[]> | undefined>;
  removeInvitation: (member: Member) => Promise<ApiResponse<[]> | undefined>;
  isCurrentUserAdmin: boolean;
  membersLimitExceeded: boolean;
};

export const MembersContext = React.createContext<ContextValue | undefined>(
  undefined
);

export const MembersProvider: React.FC<Props> = (props) => {
  const { currentWorkspaceId, currentWorkspace, setWorkspaces } =
    useWorkspaces();
  const { user } = useAuthentication();
  const [members, setMembers] = useState<Member[]>([]);

  const membersLimitExceeded = useMemo(() => {
    if (!currentWorkspace) return false;

    return (
      members.filter((item) => !item.pending && !item.deleted_at).length >
      currentWorkspace?.active_plan?.allowed_team_members
    );
  }, [currentWorkspace, members]);

  const fetchMembers = useCallback(async () => {
    if (!currentWorkspaceId) return [];

    const response = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/members`
    );

    const apiResponse: ApiResponse<Member[]> = await response.json();

    if (apiResponse.status) {
      setMembers(apiResponse.data);
      return apiResponse.data;
    }

    return [];
  }, [currentWorkspaceId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const inviteMember = useCallback(
    async (emails: string[]) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/invitations`,
        {
          method: "POST",
          body: JSON.stringify({ members: emails.map((email) => ({ email })) })
        }
      );

      const apiResponse: ApiResponse<Member[]> = await response.json();

      if (apiResponse.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.member += 1;
          })
        );

        await fetchMembers();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchMembers, setWorkspaces]
  );

  const updateMember = useCallback(
    async (member: Member, role: string) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/members`,
        {
          method: "PUT",
          body: JSON.stringify({ uuid: member.uuid, role })
        }
      );

      const apiResponse: ApiResponse<Member> = await response.json();

      await fetchMembers();

      return apiResponse;
    },
    [fetchMembers, currentWorkspaceId]
  );

  const removeMember = useCallback(
    async (member: Member) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/members/${member.uuid}`,
        {
          method: "DELETE"
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      if (apiResponse.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.member -= 1;
          })
        );

        await fetchMembers();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchMembers, setWorkspaces]
  );

  const removeInvitation = useCallback(
    async (member: Member) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/invitation/${member.uuid}`,
        {
          method: "DELETE"
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      if (apiResponse.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.member -= 1;
          })
        );

        await fetchMembers();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchMembers, setWorkspaces]
  );

  const isCurrentUserAdmin = useMemo(() => {
    const currentUserMember = find(members, { email: user?.email });
    return currentUserMember?.role === Role.admin;
  }, [members, user?.email]);

  const value = useMemo(
    () => ({
      members,
      fetchMembers,
      inviteMember,
      updateMember,
      removeMember,
      removeInvitation,
      isCurrentUserAdmin,
      membersLimitExceeded
    }),
    [
      members,
      fetchMembers,
      inviteMember,
      updateMember,
      removeMember,
      removeInvitation,
      isCurrentUserAdmin,
      membersLimitExceeded
    ]
  );

  return <MembersContext.Provider value={value} {...props} />;
};

export const useMembers = (): ContextValue => {
  const context = useContext(MembersContext);

  if (context === undefined) {
    throw new Error("useMember must be used within an MembersProvider");
  }

  return context;
};
